<template>
  <div class="w-full">
    <div
      v-if="selectedTab !== 'Summary'"
      id="chart_div"
      class="w-full ml-8"
    />
    <GChart
      v-else
      type="ColumnChart"
      :data="chartData"
      :options="chartOptions"
      :resize-debounce="500"
      class="w-full"
    />
  </div>
</template>

<script>
  import { GChart } from 'vue-google-charts';

  export default {
    components: {
      GChart,
    },

    props: {
      comparisonResults: {
        type: Object,
        default: null,
      },
      years: {
        type: Number,
        default: 5,
      },
      selectedTab: {
        type: String,
        default: 'Summary',
      },
    },

    data() {
      return {
        chartData: [],
        chartOptions: {
          // enableInteractivity: false,
          height: 650,
          colors: ['#9CA3AF', '#d4af37'],
          isStacked: false,
          bar: { groupWidth: '75%' },
          legend: {
            textStyle: {
              color: '#ffffff',
              fontName: 'Inter',
            },
            position: 'top',
          },
          hAxis: {
            textStyle: {
              color: '#ffffff',
            },
            gridlines: { color: '#6B7280' },
          },
          vAxis: {
            format: '£',
            textStyle: {
              color: '#ffffff',
              fontName: 'Inter',
            },
            // format: '£',
            gridlines: { color: '#6B7280' },
          },
          backgroundColor: { fill: 'transparent' },
        },
      };
    },

    watch: {
      selectedTab() {
        if (this.selectedTab === 'Summary') {
          this.setSummaryData();
        }

        if (this.selectedTab === 'Fixed Costs') {
          this.setFixedCostsData();
        }

        if (this.selectedTab === 'Profit') {
          this.setProfitData();
        }

        if (this.selectedTab === 'ROI') {
          this.setROIData();
        }
      },
    },

    mounted() {
      if (this.selectedTab === 'Summary') {
        this.setSummaryData();
      }

      if (this.selectedTab === 'Fixed Costs') {
        this.setFixedCostsData();
      }

      if (this.selectedTab === 'Profit') {
        this.setProfitData();
      }

      if (this.selectedTab === 'ROI') {
        this.setROIData();
      }
    },

    methods: {
      setSummaryData() {
        const p2totalCost = this.comparisonResults.Product2.AvgProjectedTotalCosts;
        const p2totalRevenue = this.comparisonResults.Product2.AvgProjectedNYTotalRevenue;
        const p2totalProfit = this.comparisonResults.Product2.ProjectedProfit.Mean;

        const p1totalCost = this.comparisonResults.Product1.AvgProjectedTotalCosts;
        const p1totalRevenue = this.comparisonResults.Product1.AvgProjectedNYTotalRevenue;
        const p1totalProfit = this.comparisonResults.Product1.ProjectedProfit.Mean;

        this.chartData = [
          ['', 'Your own', 'IX'],
          [
            'Avg. total cost',
            { v: p1totalCost, f: this.formatCurrency(p1totalCost) },
            { v: p2totalCost, f: this.formatCurrency(p2totalCost) },
          ],
          [
            'Avg. total revenue',
            { v: p1totalRevenue, f: this.formatCurrency(p1totalRevenue) },
            { v: p2totalRevenue, f: this.formatCurrency(p2totalRevenue) },
          ],
          [
            'Avg. total profit',
            { v: p1totalProfit, f: this.formatCurrency(p1totalProfit) },
            { v: p2totalProfit, f: this.formatCurrency(p2totalProfit) },
          ],
        ];
      },

      setFixedCostsData() {
        const vm = this;
        function drawChart() {
          const data = new google.visualization.DataTable();
          data.addColumn('string', 'Category');
          data.addColumn('number', 'Minimum');
          data.addColumn('number', 'Average');
          data.addColumn('number', 'Maximum');

          const p1Min = vm.comparisonResults.Product1.ProjectedFixedCosts.Min;
          const p1Mean = vm.comparisonResults.Product1.ProjectedFixedCosts.Mean;
          const p1Max = vm.comparisonResults.Product1.ProjectedFixedCosts.Max;

          const p2Min = vm.comparisonResults.Product2.ProjectedFixedCosts.Min;
          const p2Mean = vm.comparisonResults.Product2.ProjectedFixedCosts.Mean;
          const p2Max = vm.comparisonResults.Product2.ProjectedFixedCosts.Max;

          console.log(`Min: ${p2Min} - Mean: ${p2Mean} - Max: ${p2Max}`);

          data.addRows([
            [
              'Your own',
              { v: p1Min, f: vm.formatCurrency(p1Min) },
              { v: p1Mean, f: vm.formatCurrency(p1Mean) },
              { v: p1Max, f: vm.formatCurrency(p1Max) },
            ],
            [
              'IX',
              { v: p2Min, f: vm.formatCurrency(p2Min) },
              { v: p2Mean, f: vm.formatCurrency(p2Mean) },
              { v: p2Max, f: vm.formatCurrency(p2Max) },
            ],
          ]);

          const formatter = new google.visualization.NumberFormat({
            negativeColor: 'red',
            negativeParens: true,
            pattern: '£###,###',
          });
          formatter.format(data, 1);

          const view = new google.visualization.DataView(data);
          // duplicate 1 column as a dummy data series, and add intervals to it
          view.setColumns([
            0,
            1,
            {
              id: 'min',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 1);
              },
            },
            {
              id: 'avg',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 2);
              },
            },
            {
              id: 'max',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 3);
              },
            },
            1,
            2,
            3,
          ]);

          const chart = new google.visualization.LineChart(document.querySelector('#chart_div'));
          const max = Math.round(p1Max / 500) * 500;
          const min = Math.round(p1Min / 500) * 500;

          google.visualization.events.addListener(chart, 'ready', (e) => {
            chart.setSelection([
              { row: 0, column: 6 },
              { row: 1, column: 6 },
            ]);
          });

          chart.draw(view, {
            height: 650,
            backgroundColor: 'transparent',
            lineWidth: 1,
            bar: { groupWidth: '95%' },
            intervals: {
              style: 'boxes',
            },
            legend: {
              position: 'none',
            },
            hAxis: {
              textStyle: {
                color: '#ffffff',
                fontName: 'Arial',
                fontName: 'Inter',
              },
              gridlines: { color: '#6B7280' },
            },
            enableInteractivity: false,
            selectionMode: 'multiple',
            tooltip: { trigger: 'selection' },
            aggregationTarget: 'none',

            vAxis: {
              textStyle: {
                color: '#ffffff',
                fontName: 'Inter',
              },
              ticks: [
                {
                  v: max * 0.1,
                  f: vm.formatCurrency(max * 0.1),
                },
                {
                  v: max * 0.2,
                  f: vm.formatCurrency(max * 0.2),
                },
                {
                  v: max * 0.4,
                  f: vm.formatCurrency(max * 0.4),
                },
                {
                  v: max * 0.6,
                  f: vm.formatCurrency(max * 0.6),
                },
                {
                  v: max * 0.8,
                  f: vm.formatCurrency(max * 0.8),
                },
                {
                  v: p1Max,
                  f: vm.formatCurrency(p1Max),
                },
              ],
              gridlines: { color: '#6B7280' },
            },
            series: {
              0: {
                // dummy data series, controls color of intervals
                visibleInLegend: false,
                color: '#FFFFFF',
                enableInteractivity: true,
              },
              1: {
                // min series options
              },
              2: {
                // average series options
              },
              3: {
                // max series options
              },
            },
          });
        }
        google.load('visualization', '1', { packages: ['corechart'], callback: drawChart });
      },

      setProfitData() {
        const vm = this;
        function drawChart() {
          const data = new google.visualization.DataTable();
          data.addColumn('string', 'Category');
          data.addColumn('number', 'Minimum');
          data.addColumn('number', 'Average');
          data.addColumn('number', 'Maximum');

          const p1Min = vm.comparisonResults.Product1.ProjectedProfit.Min;
          const p1Mean = vm.comparisonResults.Product1.ProjectedProfit.Mean;
          const p1Max = vm.comparisonResults.Product1.ProjectedProfit.Max;

          const p2Min = vm.comparisonResults.Product2.ProjectedProfit.Min;
          const p2Mean = vm.comparisonResults.Product2.ProjectedProfit.Mean;
          const p2Max = vm.comparisonResults.Product2.ProjectedProfit.Max;

          data.addRows([
            [
              'Your own',
              { v: p1Min, f: vm.formatCurrency(p1Min) },
              { v: p1Mean, f: vm.formatCurrency(p1Mean) },
              { v: p1Max, f: vm.formatCurrency(p1Max) },
            ],
            [
              'IX',
              { v: p2Min, f: vm.formatCurrency(p2Min) },
              { v: p2Mean, f: vm.formatCurrency(p2Mean) },
              { v: p2Max, f: vm.formatCurrency(p2Max) },
            ],
          ]);

          const formatter = new google.visualization.NumberFormat({
            negativeColor: 'red',
            negativeParens: true,
            pattern: '£###,###',
          });
          formatter.format(data, 1);

          const view = new google.visualization.DataView(data);
          // duplicate 1 column as a dummy data series, and add intervals to it
          view.setColumns([
            0,
            1,
            {
              id: 'min',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 1);
              },
            },
            {
              id: 'avg',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 2);
              },
            },
            {
              id: 'max',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 3);
              },
            },
            1,
            2,
            3,
          ]);

          const chart = new google.visualization.LineChart(document.querySelector('#chart_div'));

          const min = p1Min < p2Min ? Math.floor(p1Min / 500) * 500 : Math.floor(p2Min / 500) * 500;
          const max = p1Max < p2Max ? Math.floor(p2Max / 500) * 500 : Math.floor(p1Max / 500) * 500;

          google.visualization.events.addListener(chart, 'ready', (e) => {
            chart.setSelection([
              { row: 0, column: 6 },
              { row: 1, column: 6 },
            ]);
          });

          chart.draw(view, {
            height: 650,
            backgroundColor: 'transparent',
            lineWidth: 1,
            bar: { groupWidth: '95%' },
            intervals: {
              style: 'boxes',
            },
            legend: {
              position: 'none',
            },
            hAxis: {
              textStyle: {
                color: '#ffffff',
                fontName: 'Arial',
                fontName: 'Inter',
              },
              gridlines: { color: '#6B7280' },
            },
            enableInteractivity: false,
            selectionMode: 'multiple',
            tooltip: { trigger: 'selection' },
            aggregationTarget: 'none',

            vAxis: {
              textStyle: {
                color: '#ffffff',
                fontName: 'Inter',
              },
              ticks: [
                {
                  v: 0,
                  f: '£0',
                },
                {
                  v: max * 0.2,
                  f: vm.formatCurrency(max * 0.2),
                },
                {
                  v: max * 0.4,
                  f: vm.formatCurrency(max * 0.4),
                },
                {
                  v: max * 0.6,
                  f: vm.formatCurrency(max * 0.6),
                },
                {
                  v: max * 0.8,
                  f: vm.formatCurrency(max * 0.6),
                },
                {
                  v: max,
                  f: vm.formatCurrency(max),
                },
              ],
              gridlines: { color: '#6B7280' },
            },
            series: {
              0: {
                // dummy data series, controls color of intervals
                visibleInLegend: false,
                color: '#FFFFFF',
                enableInteractivity: false,
              },
              1: {
                // min series options
              },
              2: {
                // average series options
              },
              3: {
                // max series options
              },
            },
          });
        }
        google.load('visualization', '1', { packages: ['corechart'], callback: drawChart });
      },

      setROIData() {
        const vm = this;
        function drawChart() {
          const data = new google.visualization.DataTable();
          data.addColumn('string', 'Category');
          data.addColumn('number', 'Minimum');
          data.addColumn('number', 'Average');
          data.addColumn('number', 'Maximum');

          const p1Min = vm.comparisonResults.Product1.ProjectedROI.Min * 100;
          const p1Mean = vm.comparisonResults.Product1.ProjectedROI.Mean * 100;
          const p1Max = vm.comparisonResults.Product1.ProjectedROI.Max * 100;

          const p2Min = vm.comparisonResults.Product2.ProjectedROI.Min * 100;
          const p2Mean = vm.comparisonResults.Product2.ProjectedROI.Mean * 100;
          const p2Max = vm.comparisonResults.Product2.ProjectedROI.Max * 100;

          data.addRows([
            [
              'Your own',
              { v: p1Min, f: vm.formatPercentage(p1Min) },
              { v: p1Mean, f: vm.formatPercentage(p1Mean) },
              { v: p1Max, f: vm.formatPercentage(p1Max) },
            ],
            [
              'IX',
              { v: p2Min, f: vm.formatPercentage(p2Min) },
              { v: p2Mean, f: vm.formatPercentage(p2Mean) },
              { v: p2Max, f: vm.formatPercentage(p2Max) },
            ],
          ]);

          const view = new google.visualization.DataView(data);
          // duplicate 1 column as a dummy data series, and add intervals to it
          view.setColumns([
            0,
            1,
            {
              id: 'min',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 1);
              },
            },
            {
              id: 'avg',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 2);
              },
            },
            {
              id: 'max',
              type: 'number',
              role: 'interval',
              calc(dt, row) {
                return dt.getValue(row, 3);
              },
            },
            1,
            2,
            3,
          ]);

          const chart = new google.visualization.LineChart(document.querySelector('#chart_div'));

          let min = p1Min < p2Min ? p1Min : p2Min;
          let max = p1Max < p2Max ? p2Max : p1Max;

          min = Math.round(min / 10) * 10;
          max = Math.round(max / 10) * 10;

          google.visualization.events.addListener(chart, 'ready', (e) => {
            chart.setSelection([
              { row: 0, column: 6 },
              { row: 1, column: 6 },
            ]);
          });

          chart.draw(view, {
            height: 650,
            backgroundColor: 'transparent',
            lineWidth: 1,
            bar: { groupWidth: '95%' },
            intervals: {
              style: 'boxes',
            },
            legend: {
              position: 'none',
            },
            hAxis: {
              textStyle: {
                color: '#ffffff',
                fontName: 'Inter',
              },
              gridlines: { color: '#6B7280' },
            },
            enableInteractivity: false,
            selectionMode: 'multiple',
            tooltip: { trigger: 'selection' },
            aggregationTarget: 'none',

            vAxis: {
              textStyle: {
                color: '#ffffff',
                fontName: 'Inter',
              },
              ticks: [
                {
                  v: min,
                  f: vm.formatPercentage(min),
                },
                {
                  v: max * 0.2,
                  f: vm.formatPercentage(max * 0.2),
                },
                {
                  v: max * 0.4,
                  f: vm.formatPercentage(max * 0.4),
                },
                {
                  v: max * 0.6,
                  f: vm.formatPercentage(max * 0.6),
                },
                {
                  v: max * 0.8,
                  f: vm.formatPercentage(max * 0.6),
                },
                {
                  v: max,
                  f: vm.formatPercentage(max),
                },
              ],
              gridlines: { color: '#6B7280' },
            },
            series: {
              0: {
                // dummy data series, controls color of intervals
                visibleInLegend: false,
                color: '#FFFFFF',
                enableInteractivity: false,
              },
              1: {
                // min series options
              },
              2: {
                // average series options
              },
              3: {
                // max series options
              },
            },
          });
        }
        google.load('visualization', '1', { packages: ['corechart'], callback: drawChart });
      },

      formatCurrency(value) {
        return value
          .toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
          .split('.')[0];
      },

      formatPercentage(value) {
        return `${parseFloat(value.toFixed(2))}%`;
      },
    },
  };
</script>
